import type { MutableRefObject } from "react";
import { ArrowResetRegular, WarningRegular, ZoomInRegular, ZoomOutRegular } from "@fluentui/react-icons";
import { LOADING_MESSAGES, PDF_CONFIG, ZOOM_LEVEL_OPTIONS } from "../../../constants";
import styles from "./DetailView.module.css";

interface PdfViewerProps {
  readonly isWordOperationActive: boolean;
  readonly isLoadingPdf: boolean;
  readonly pdfError: string;
  readonly pdfLoaded: boolean;
  readonly totalPages: number;
  readonly zoomLevel: number;
  readonly canvasRefs: MutableRefObject<(HTMLCanvasElement | null)[]>;
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly onResetZoom: () => void;
  readonly onZoomChange: (level: number) => void;
  readonly onRetry: () => void;
}

export function PdfViewer({
  isWordOperationActive,
  isLoadingPdf,
  pdfError,
  pdfLoaded,
  totalPages,
  zoomLevel,
  canvasRefs,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onZoomChange,
  onRetry,
}: PdfViewerProps) {
  if (isWordOperationActive) {
    return (
      <div className={styles.loadingState}>
        <span className={styles.spinnerLarge} aria-hidden="true" />
        <p className={styles.loadingText}>{LOADING_MESSAGES.OPENING}</p>
      </div>
    );
  }

  if (isLoadingPdf) {
    return (
      <div className={styles.loadingState}>
        <span className={styles.spinnerLarge} aria-hidden="true" />
        <p className={styles.loadingText}>{LOADING_MESSAGES.PDF}</p>
      </div>
    );
  }

  if (pdfError) {
    return (
      <div className={styles.emptyState}>
        <WarningRegular className={styles.emptyIconError} aria-hidden="true" />
        <h3 className={styles.emptyTitle}>Error Loading PDF</h3>
        <p className={styles.emptyMessage}>{pdfError}</p>
        <button type="button" className={styles.btn} onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  if (!pdfLoaded || totalPages <= 0) {
    return null;
  }

  return (
    <div className={styles.pdfViewerContainer}>
      <div className={styles.pdfViewerContent}>
        <div
          className={styles.pdfPagesContainer}
          style={{ transform: `scale(${zoomLevel})`, transformOrigin: "top left" }}
        >
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum, index) => (
            <div key={pageNum} className={styles.pdfPageWrapper}>
              <canvas
                ref={(el) => {
                  canvasRefs.current[index] = el;
                }}
                className={styles.pdfCanvas}
                aria-label={`Page ${pageNum} of ${totalPages}`}
              />
              <div className={styles.pageLabel}>Page {pageNum}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.pdfZoomControls}>
        <button
          type="button"
          className={styles.zoomBtn}
          onClick={onZoomOut}
          disabled={zoomLevel <= PDF_CONFIG.MIN_ZOOM}
          title="Zoom Out"
          aria-label="Zoom out"
        >
          <ZoomOutRegular />
        </button>
        <select
          className={styles.zoomSelect}
          value={Math.round(zoomLevel * 100)}
          onChange={(e) => onZoomChange(Number(e.target.value) / 100)}
          aria-label="Zoom level"
        >
          {ZOOM_LEVEL_OPTIONS.map((pct) => (
            <option key={pct} value={pct}>
              {pct}%
            </option>
          ))}
        </select>
        <button
          type="button"
          className={styles.zoomBtn}
          onClick={onZoomIn}
          disabled={zoomLevel >= PDF_CONFIG.MAX_ZOOM}
          title="Zoom In"
          aria-label="Zoom in"
        >
          <ZoomInRegular />
        </button>
        <button type="button" className={styles.zoomBtn} onClick={onResetZoom} title="Reset Zoom" aria-label="Reset zoom to 100%">
          <ArrowResetRegular />
        </button>
      </div>
    </div>
  );
}
